import React from "react";

// Static legal pages — wellness (non-medical) positioning.
const UPDATED = "June 2026";

function LegalShell({ eyebrow, title, children }) {
  return (
    <main id="main" className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="legal-page">
      <a href="/" className="iw-mono uppercase" style={{ color: "var(--iw-accent-red)", fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 }} data-testid="legal-back-link">
        ← Back to Integrated Wellness
      </a>
      <div className="iw-mono uppercase mt-6 mb-2" style={{ color: "var(--iw-accent-red)", fontSize: "10px", letterSpacing: "0.22em", fontWeight: 600 }}>
        {eyebrow}
      </div>
      <h1 className="serif font-bold m-0" style={{ color: "var(--iw-primary)", fontSize: "clamp(30px,6vw,44px)" }}>
        {title}
      </h1>
      <p className="iw-mono uppercase mt-3" style={{ color: "var(--iw-muted)", fontSize: "10px", letterSpacing: "0.18em" }}>
        Last updated {UPDATED}
      </p>
      <div className="mt-8 legal-body" style={{ color: "var(--iw-body)" }}>
        {children}
      </div>
    </main>
  );
}

function H2({ children }) {
  return <h2 className="serif font-bold mt-9 mb-3" style={{ color: "var(--iw-primary)", fontSize: "20px" }}>{children}</h2>;
}
function P({ children }) {
  return <p className="text-base leading-relaxed mb-4" style={{ color: "var(--iw-body)" }}>{children}</p>;
}
function LI({ children }) {
  return <li className="text-base leading-relaxed mb-2" style={{ color: "var(--iw-body)" }}>{children}</li>;
}

export function PrivacyPage() {
  return (
    <LegalShell eyebrow="Privacy" title="Privacy Policy">
      <P>
        Integrated Wellness ("we") operates the Nervous System Reset wellness tool. This policy explains
        what we collect, why, and the choices you have. This is a <strong>wellness, non-medical</strong>{" "}
        service and is not a substitute for professional medical or mental-health care.
      </P>

      <H2>What we collect</H2>
      <ul className="list-disc pl-5">
        <LI><strong>Account details</strong> — the member ID and email your wellness team sets up for you, used only to sign you in.</LI>
        <LI><strong>Session data</strong> — the self-reported check-in ratings you enter (e.g. activation, mood), how long a session ran, whether you completed the grounding step, and any notes or reflections you choose to save.</LI>
        <LI><strong>Recovery check-ins</strong> — the wellness ratings, and any optional wearable numbers (heart rate, HRV, sleep) you choose to enter manually.</LI>
        <LI><strong>Coach messages</strong> — text you send to the in-app wellness concierge.</LI>
      </ul>

      <H2>How we use it</H2>
      <P>
        We use your information to run your sessions, show you your own progress over time, and share a
        summary with your Integrated Wellness care team so they can support you. We do not sell your data
        or use it for advertising.
      </P>

      <H2>How it is protected</H2>
      <ul className="list-disc pl-5">
        <LI>You sign in over a secure, encrypted connection using an httpOnly session cookie — no password or token is stored in your browser's local storage.</LI>
        <LI>Your reset history is scoped to your account; other members cannot see it.</LI>
        <LI>Records shared with your care team are stored in the practice's Integrated Wellness workspace.</LI>
      </ul>

      <H2>Your choices</H2>
      <P>
        You can ask your wellness team to view, correct, or delete your records at any time. Using a
        session is always optional and you can stop at any point.
      </P>

      <H2>Crisis resources</H2>
      <P>
        This tool cannot respond to emergencies. If you are in crisis, call or text{" "}
        <strong>988</strong> (Suicide &amp; Crisis Lifeline), or call <strong>911</strong>.
      </P>

      <H2>Contact</H2>
      <P>
        Questions about your privacy? Reach out to your Integrated Wellness care team through your usual
        channel.
      </P>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell eyebrow="Terms" title="Terms of Use">
      <H2>Wellness, not medical care</H2>
      <P>
        The Nervous System Reset tool provides guided relaxation and self-regulation wellness exercises.
        It is <strong>not therapy, not a medical device, and does not diagnose, treat, or cure</strong>{" "}
        any condition. It is not a substitute for care from a licensed professional.
      </P>

      <H2>Who can use it</H2>
      <P>
        Access is provided to members whose account has been created by the Integrated Wellness team.
        Please keep your member ID and email confidential and do not share your access.
      </P>

      <H2>Safety</H2>
      <ul className="list-disc pl-5">
        <LI>Before each session a brief safety check may pause the experience and point you to appropriate support.</LI>
        <LI>Stop any session at any time. Use the tool in a safe, quiet place — not while driving or operating machinery.</LI>
        <LI>If you are in crisis, call or text <strong>988</strong> or call <strong>911</strong>.</LI>
      </ul>

      <H2>Acceptable use</H2>
      <P>
        Please use the tool for your own wellness only. Automated scraping, abuse of the coach features,
        or attempts to access other members' data are not permitted.
      </P>

      <H2>No guarantees</H2>
      <P>
        Wellness responses vary from person to person. We make no promises about specific outcomes, and
        the tool is provided on an "as is" basis to the extent permitted by law.
      </P>

      <H2>Changes</H2>
      <P>
        We may update these terms as the tool evolves. Continued use after an update means you accept the
        current version.
      </P>
    </LegalShell>
  );
}
